import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, MapPin, MessageCircle, Phone, CheckCircle2, Clock, Sparkles, Mountain } from 'lucide-react';
import { DOMESTIC_DESTINATIONS, DOMESTIC_FAQS, PACKAGES, COMPANY } from '../mock';
import { PackageCard } from '../components/sections/Packages';
import { PageHero } from './DestinationsPages';
import { Button } from '../components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import EnquiryForm from '../components/sections/EnquiryForm';
import { CTABanner } from '../components/sections/CTASections';
import VideoGallery from '../components/sections/VideoGallery';
import ReviewCards from '../components/sections/ReviewCards';
import CustomerVideoReviews from '../components/sections/CustomerVideoReviews';
import PerfectDuration from '../components/sections/PerfectDuration';
import SummerOffers from '../components/sections/SummerOffers';
import { AllInclusiveGrid, ExperienceGallery, TrustBanner } from '../components/sections/EngagementSections';
import TravelerTypes from '../components/sections/TravelerTypes';
import HeroSection from '../components/sections/HeroSection';
import { DESTINATION_SPECIFIC_DATA } from '../constants/destinationData';
import { getVideoReviews, VIDEO_REVIEWS } from '../constants/videoReviews';

const DESTINATION_VIDEOS = {
  'jammu-kashmir': VIDEO_REVIEWS.kashmir.map((v) => ({ id: v.id, title: v.destination })),
  'leh-ladakh': VIDEO_REVIEWS['leh-ladakh'].map((v) => ({ id: v.id, title: v.destination })),
  'himachal-pradesh': VIDEO_REVIEWS.himachal.map((v) => ({ id: v.id, title: v.destination })),
  'spiti-kinnaur': VIDEO_REVIEWS['spiti-kinnaur'].map((v) => ({ id: v.id, title: v.destination })),
};

export function DomesticDestinationsPage() {
  return (
    <div>
      <Helmet>
        <title>Domestic Tour Packages | Mountains Chain Travel</title>
        <meta name="description" content="Discover breathtaking domestic tours across India. Explore Himachal, Kashmir, Ladakh, Uttarakhand, Kerala, and more with Mountains Chain Travel." />
        <meta property="og:title" content="Domestic Tour Packages | Mountains Chain Travel" />
        <meta property="og:description" content="Discover breathtaking domestic tours across India. Explore Himachal, Kashmir, Ladakh, Uttarakhand, Kerala, and more with Mountains Chain Travel." />
      </Helmet>

      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <img src={DOMESTIC_DESTINATIONS[3].image} alt="Domestic Destinations" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/85 via-brand-blue/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 via-transparent to-brand-blue/30" />
        <div className="relative z-20 max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2.5 px-5 py-2 bg-brand-orange rounded-full text-white text-[13px] font-bold mb-6 shadow-lg shadow-brand-orange/40">
              <span className="tracking-wide">Explore Incredible India</span>
            </div>
            <h1 className="font-poppins text-[48px] sm:text-[56px] lg:text-[72px] text-white leading-[1.05] font-extrabold drop-shadow-2xl">
              <span className="text-brand-orange">Domestic</span>
              <br />
              <span>Destinations</span>
            </h1>
            <p className="text-white text-[18px] lg:text-[21px] mt-5 max-w-xl leading-relaxed font-medium drop-shadow-lg">
              From Himalayan peaks to tropical backwaters - discover the magic of incredible India with hand-crafted journeys.
            </p>
            <div className="mt-6 inline-flex flex-wrap items-center gap-2 px-4 py-2.5 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
              <span className="bg-brand-orange text-white text-[12px] font-bold px-2.5 py-1 rounded-md uppercase tracking-wider">Regions</span>
              <span className="text-white text-[14px] lg:text-[15px] font-semibold">
                Himachal · Kashmir · Ladakh · Uttarakhand · Rajasthan · Kerala
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Review Cards - Right after hero */}
      <ReviewCards />
      <CustomerVideoReviews />

      <section className="max-w-7xl mx-auto px-6 pt-10">
        <div className="flex flex-wrap gap-2 mb-6">
          <Link to="/destinations" className="px-4 py-2 rounded-full text-[13px] font-semibold bg-brand-grey text-brand-blue hover:bg-brand-greyDark transition-colors">All Destinations</Link>
          <span className="px-4 py-2 rounded-full text-[13px] font-semibold bg-brand-blue text-white">Domestic</span>
          <Link to="/international" className="px-4 py-2 rounded-full text-[13px] font-semibold bg-brand-grey text-brand-blue hover:bg-brand-greyDark transition-colors">International</Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">13 Regions</div>
            <h2 className="font-poppins text-[28px] lg:text-[36px] text-brand-blue leading-[1.1]">Explore <span className="text-brand-orange">incredible India</span></h2>
          </div>
          <div className="hidden md:flex items-center gap-2 text-[13px] text-brand-blue/65"><Mountain size={16} className="text-brand-orange"/>From mountains to backwaters</div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {DOMESTIC_DESTINATIONS.map((d, i) => (
            <Link key={d.slug} to={`/domestic/${d.slug}`} className="group rounded-2xl overflow-hidden bg-white border border-brand-greyDark shadow-card card-hover img-zoom-wrap relative h-[340px] animate-fade-up" style={{animationDelay: `${i*60}ms`}}>
              <img src={d.image} alt={d.name} className="absolute inset-0 w-full h-full object-cover img-zoom"/>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-blue via-brand-blue/40 to-transparent"/>
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-sm px-2.5 py-1 rounded-md text-[11px] font-bold text-brand-blue">
                <MapPin size={11} className="text-brand-orange"/>{d.region}
              </div>
              <div className="absolute top-4 right-4 bg-brand-orange text-white text-[11px] font-bold px-2.5 py-1 rounded-md">From ₹{d.startingPrice.toLocaleString('en-IN')}</div>
              <div className="relative h-full p-5 flex flex-col justify-end text-white">
                <h3 className="font-poppins font-bold text-[24px] leading-tight capitalize">{d.name}</h3>
                <p className="text-white/85 text-[13px] mt-1.5 line-clamp-2">{d.tagline}</p>
                <div className="mt-3 flex items-center gap-2 text-brand-orange font-semibold text-[13px] group-hover:gap-3 transition-all">
                  Explore {d.name.split(' ')[0]} Tours <ArrowRight size={14}/>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-brand-grey py-12">
        <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            {icon: MapPin, t: 'Local Experts', d: 'Guides who live there'},
            {icon: Sparkles, t: 'Hand-picked Stays', d: 'Verified hotels only'},
            {icon: CheckCircle2, t: 'Best Price', d: 'We beat any quote'},
            {icon: Clock, t: '24x7 Support', d: 'Before, during, after'},
          ].map((x, i) => (
            <div key={i} className="bg-white rounded-2xl p-5 flex items-center gap-4 border border-brand-greyDark">
              <div className="w-12 h-12 rounded-xl bg-brand-orange/10 flex items-center justify-center shrink-0"><x.icon size={22} className="text-brand-orange"/></div>
              <div><div className="font-poppins font-semibold text-brand-blue text-[15px]">{x.t}</div><div className="text-[12.5px] text-brand-blue/60">{x.d}</div></div>
            </div>
          ))}
        </div>
      </section>

      <CTABanner/>

      <section className="max-w-4xl mx-auto px-6 py-16 lg:py-20">
        <div className="text-center mb-8">
          <div className="text-brand-orange text-[13px] font-semibold tracking-[0.18em] uppercase mb-2">FAQ</div>
          <h2 className="font-poppins text-[30px] lg:text-[40px] text-brand-blue leading-[1.1]">Frequently Asked <span className="text-brand-orange">Questions</span></h2>
          <p className="text-brand-blue/65 mt-3 text-[14.5px]">Everything you need to know about booking domestic tours with us.</p>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {DOMESTIC_FAQS.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-brand-greyDark">
              <AccordionTrigger className="text-left text-brand-blue font-semibold text-[15px] lg:text-[16px] hover:text-brand-orange hover:no-underline py-5">{f.q}</AccordionTrigger>
              <AccordionContent className="text-brand-blue/70 text-[14.5px] leading-relaxed pb-5 whitespace-pre-line">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <EnquiryForm/>
    </div>
  );
}

export function DomesticDestinationDetailPage() {
  const { slug } = useParams();
  const dest = DOMESTIC_DESTINATIONS.find(d => d.slug === slug);
  if (!dest) return (
    <div className="max-w-7xl mx-auto px-6 py-20 text-center text-brand-blue">
      Destination not found. <Link to="/domestic" className="text-brand-orange underline">Back to domestic destinations</Link>
    </div>
  );
  const similar = DOMESTIC_DESTINATIONS.filter(d => d.slug !== slug).slice(0, 4);
  const pkgs = PACKAGES.filter(p => p.destination === slug);
  const isHimachal = slug === 'himachal-pradesh';
  const hasExtraData = !!DESTINATION_SPECIFIC_DATA[slug];

  const META_BY_SLUG = {
    'jammu-kashmir': { title: 'Kashmir Packages - Srinagar Gulmarg Pahalgam Starting From ₹8,999', desc: 'Srinagar Gulmarg Pahalgam. Starting From ₹8,999. Explore the best Kashmir tour packages with Mountains Chain Travel.' },
    'leh-ladakh': { title: 'Leh Ladakh Tour - Expert Local Assistance - 2 To 10 Day Packages', desc: 'Expert Local Assistance. Book With 20% Advance. 2 To 10 Day Packages. Budget To Premium Trips. Hotels Meals Transfers. Permits & Support Included.' },
  };
  const meta = META_BY_SLUG[slug];

  return (
    <div>
      <Helmet>
        <title>{meta ? meta.title : `${dest.name} Tour Packages | Mountains Chain Travel`}</title>
        <meta name="description" content={meta ? meta.desc : `Explore the best ${dest.name} tour packages with Mountains Chain Travel. ${dest.tagline} Tailored for your budget and style.`} />
        <meta property="og:title" content={meta ? meta.title : `${dest.name} Tour Packages | Mountains Chain Travel`} />
        <meta property="og:description" content={meta ? meta.desc : `Explore the best ${dest.name} tour packages with Mountains Chain Travel. ${dest.tagline} Tailored for your budget and style.`} />
      </Helmet>
      <HeroSection slug={slug} name={dest.name} startingPrice={dest.startingPrice} />
      <ReviewCards destinationKey={slug} />
      <CustomerVideoReviews reviews={getVideoReviews(slug)} />
      
      <TrustBanner />

      <section id={slug} className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-brand-orange/10 text-brand-orange rounded-full text-[12px] font-bold uppercase tracking-wider">
              <MapPin size={13}/> {dest.region} · India
            </div>
            <div id={slug === 'leh-ladakh' ? 'plan-ladakh-trip' : slug === 'jammu-kashmir' ? 'kashmir-packages' : undefined}>
              <h2 className="font-poppins text-[28px] text-brand-blue mt-4 mb-3 capitalize">About {dest.name}</h2>
              <p className="text-brand-blue/75 text-[15px] leading-relaxed">From iconic landmarks to hidden corners, our {dest.name} tours are crafted by destination specialists who have personally explored every region. Every package is fully customizable to your dates, group size, hotel preference and budget. We handle transfers, permits (where needed), sightseeing and 24x7 on-trip support.</p>
            </div>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-8 mb-3">Top experiences in {dest.name}</h3>
            <div className="flex flex-wrap gap-2">
              {dest.highlights.map(h => (
                <span key={h} className="px-3 py-1.5 bg-brand-grey rounded-full text-[13px] text-brand-blue font-medium flex items-center gap-1.5">
                  <MapPin size={12} className="text-brand-orange"/>{h}
                </span>
              ))}
            </div>

            <h3 className="font-poppins text-[20px] text-brand-blue mt-8 mb-4">What's included</h3>
            <ul className="space-y-2.5">
              {['Hand-picked hotel stay', 'AC vehicle transfers & sightseeing', 'Daily breakfast (some packages include dinner)', 'Permits where required (ILP/PAP)', 'Local English / Hindi speaking guide', '24x7 on-trip WhatsApp helpline', 'GST & service charges'].map((x, i) => (
                <li key={i} className="flex items-start gap-3 text-brand-blue/80 text-[14.5px]">
                  <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 shrink-0"/>{x}
                </li>
              ))}
            </ul>

            {slug === 'leh-ladakh' && <div id="whats-included" />}
            {isHimachal && (
              <div className="mt-8 p-5 rounded-2xl bg-brand-orange/5 border border-brand-orange/20">
                <div className="font-poppins font-semibold text-brand-blue mb-1">Looking for specific Himachal tours?</div>
                <p className="text-brand-blue/70 text-[13.5px] mb-3">We have 9 hand-crafted itineraries - Manali, Shimla, Kasol, Spiti, Bir Billing & more.</p>
                <Link to="/himachal-tours" className="inline-flex items-center gap-2 text-brand-orange font-semibold text-[14px] border-b-2 border-brand-orange pb-0.5 hover:gap-3 transition-all">View all 9 Himachal tours <ArrowRight size={14}/></Link>
              </div>
            )}
          </div>

          <aside className="bg-brand-grey rounded-2xl p-6 h-fit lg:sticky lg:top-24">
            <div className="flex items-center gap-2 text-brand-orange text-[11px] font-bold uppercase tracking-wider mb-3"><Clock size={14}/>Plan Your Trip</div>
            <p className="text-brand-blue/70 text-[13.5px] mb-4">Get a custom itinerary in minutes. Free, no obligation - a real travel designer replies on WhatsApp.</p>
            <div className="flex flex-col gap-2.5 mt-2">
              <a href={`https://wa.me/${COMPANY.whatsapp}?text=${encodeURIComponent('Hi Mountains Chain Travel! 👋\n\nI\'m planning a ' + dest.name + ' trip and would love a customized itinerary.\n\nPlease share:\n- Available tour packages\n- Hotel options & pricing\n- Best time to visit\n\nThank you!')}`} target="_blank" rel="noreferrer">
                <Button className="w-full bg-brand-orange hover:bg-brand-orangeDark text-white h-11 rounded-xl btn-glow"><MessageCircle size={16} className="mr-2"/>Get Custom Quote</Button>
              </a>
              <a href={`tel:${COMPANY.phone}`}>
                <Button variant="outline" className="w-full border-brand-blue/20 text-brand-blue hover:bg-brand-blue hover:text-white h-11 rounded-xl"><Phone size={16} className="mr-2"/>Call to Customize</Button>
              </a>
            </div>
            <div className="mt-5 pt-5 border-t border-brand-greyDark space-y-2.5 text-[12.5px] text-brand-blue/75">
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Free cancellation up to 14 days</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>Pay just 20% to book</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>0% EMI available</div>
              <div className="flex items-start gap-2"><CheckCircle2 size={14} className="text-brand-orange mt-0.5"/>24x7 on-trip support</div>
            </div>
          </aside>
        </div>

        {hasExtraData && (
          <div className="mt-16 -mx-6">
            <TravelerTypes slug={slug} />
            <div id={slug === 'leh-ladakh' ? 'ladakh-trip-duration' : undefined}><PerfectDuration slug={slug} /></div>
            <AllInclusiveGrid slug={slug} />
            <ExperienceGallery slug={slug} />
            <SummerOffers slug={slug} />
          </div>
        )}

        <div className="mt-16">
          <h2 className="font-poppins text-[26px] text-brand-blue mb-6">Other Indian destinations</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {similar.map(s => (
              <Link key={s.slug} to={`/domestic/${s.slug}`} className="group rounded-xl overflow-hidden img-zoom-wrap relative h-44">
                <img src={s.image} alt={s.name} className="absolute inset-0 w-full h-full object-cover img-zoom"/>
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue to-transparent"/>
                <div className="relative h-full flex flex-col justify-end p-3 text-white">
                  <div className="text-[10.5px] text-white/75 uppercase tracking-wider">{s.region}</div>
                  <h4 className="font-poppins font-bold text-[15px] leading-tight">{s.name}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {pkgs.length > 0 && (
          <div className="mt-16">
            <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
              <h2 className="font-poppins text-[26px] lg:text-[34px] text-brand-blue">{dest.name} Tour Packages</h2>
              <Link to="/packages" className="text-brand-orange font-semibold flex items-center gap-1">All packages <ArrowRight size={15}/></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {pkgs.map(p => <PackageCard key={p.id} pkg={p} />)}
            </div>
          </div>
        )}

        {DESTINATION_VIDEOS[slug] && (
          <div className="mt-16 border-t pt-16">
            <VideoGallery 
              videos={DESTINATION_VIDEOS[slug]} 
              title={<>Experience {dest.name} in <span className="text-brand-orange">4K</span></>}
              sub={`Real traveler footage from our recent ${dest.name} groups. Get a glimpse of what awaits you!`}
            />
          </div>
        )}
      </section>
      <EnquiryForm/>
    </div>
  );
}

